// ----------------------------------------------------------------------
// <copyright file="Izenda.TenantDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { getOr, head, map, isUndefined } from 'lodash/fp';
import { observable, action, toJS, isObservableArray } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';

import TenantConstants from 'logic/data/Izenda.TenantConstants';
import PermissionDetail from './Izenda.PermissionDetail';
import PermissionAccess from './Izenda.Access';
import { isNil } from 'lodash';

@tracker
export default class TenantDetail {
  static constraints = {
    tenantID: TenantConstants.TENANT_ID_CONSTRAINT,
    name: TenantConstants.TENANT_NAME_CONSTRAINT
  };

  @observable id = null;
  @observable tenantID = '';
  @observable name = '';
  @observable description = '';
  @observable tenantGroups = [];
  @observable active = null;
  @observable deleted = null;
  @observable tenantModules = [];
  @observable isSelected = false;
  @observable permission = null;
  @observable version = 0;
  @observable created = null;
  @observable createdBy = null;
  @observable modified = null;
  @observable  modifiedBy = null;
  permissionAccessModel = null;

  constructor(details) {
    this.permissionAccessModel = new PermissionAccess();
    this.populateData(details || {});
  }

  get hasDirty () {
    return this.isDirty || this.permission.isDirty || this.permissionAccessModel.checkDirty();
  }

  get displayName() {
    return `${this.tenantID} - ${this.name}`;
  }

  get isNew() {
    return !String.isGuidFormat(this.id);
  }

  /**
   * tenantGroup is shortcut to first element of tenantGroups collection
   * @returns {TenantGroupingDetail}
   */
  get tenantGroup() {
    return head(this.tenantGroups);
  };

  /**
   * tenantGroup is shortcut to first element of tenantGroups collection
   * @param {TenantGroupingDetail} value
   */
  set tenantGroup(value) {
    if (isObservableArray(this.tenantGroups))
      this.tenantGroups.clear();
    else
      this.tenantGroups = [];

    if(!isNil(value))
      this.tenantGroups.push(value);
  }

  /**
   * populate data
   * @param  {object} details
   */
  @action
  populateData(details) {
    this.id = details.id;
    this.tenantID = details.tenantID;
    this.name = details.name;
    this.description = details.description;
    this.active = isUndefined(details.active) ? true : details.active;
    this.deleted = details.deleted;
    this.tenantModules = details.tenantModules;
    this.permission = new PermissionDetail(details.permission, true);
    this.tenantGroups = details.tenantGroups || [];
    this.version = details.version || 0;
    this.created = details.created;
    this.createdBy = details.createdBy;
    this.modified = details.modified;
    this.modifiedBy = details.modifiedBy;
  }

  /**
   * Serialize tenant before send to backend
   * @returns {Object}
   */
  toJson() {
    return toJS(this);
  }

  /**
   * Set data by key
   * @param {string} key
   * @param {any} value
   */
  setDataByKey(key, value) {
    this[key] = value;
    this.markDirty();
  }

  /**
   * update selected modules
   * @param  {object}  module
   * @param  {Boolean} isAdd
   */
  updateModules(module, isAdd) {
    const tenantModules = (this.tenantModules = this.tenantModules || []);
    this.markDirty();

    if (isAdd) {
      return !this.hasModule(module) && tenantModules.push(module.name);
    } else {
      const moduleIndex = tenantModules.indexOf(module.name);
      return moduleIndex > -1 && tenantModules.splice(moduleIndex, 1);
    }
  }

  /**
   * check the tenant detail has module
   * @param  {[Object]} module
   * @return {boolean}
   */
  hasModule(module) {
    return this.tenantModules && this.tenantModules.indexOf(module.name) > -1;
  }

  /**
   * set the tenant selected
   * @param {boolean} newValue
   */
  setSelected(newValue) {
    return (this.isSelected = newValue);
  }

  /**
   * set tenant detail active
   * @param {boolean} newValue
   */
  setActive(newValue) {
    return (this.active = newValue);
  }

  /**
   * Update permission
   * @param {Object} permission
   */
  updatePermission(permission) {
    this.permission = permission;
  }

  /**
   * Clean model dirty
   */
  cleanDirty() {
    this.resetDirty();
    this.permission.cleanDirty();
    this.permissionAccessModel.cleanDirty();
  }

  /**
   * Update tenant version after save
   */
  updateVersion() {
    this.version++;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.TenantDetails.js